import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  Box,
  Button,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Autocomplete,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import Swal from "sweetalert2";

const API_BASE = "http://127.0.0.1:8000"; // Change as needed

const USER_TYPES = ['registry', 'business manager', 'vpi', 'vpa', 'dean'];

export default function AdministrativeSalary() {
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);

  const [formData, setFormData] = useState({
    user: null,
    qualification: "",
    subjects: "",
    grade_class: "",
    dependent_student: "",
    contact: "",
    salary: "",
    date_add: "",
  });

  const [records, setRecords] = useState([]);
  const [loadingSubmit, setLoadingSubmit] = useState(false);

  // For edit dialog
  const [editingId, setEditingId] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [editFormData, setEditFormData] = useState({
    user: null,
    qualification: "",
    subjects: "",
    grade_class: "",
    dependent_student: "",
    contact: "",
    salary: "",
    date_add: "",
  });
  const [loadingEditSubmit, setLoadingEditSubmit] = useState(false);

  const token = localStorage.getItem("access");

  // Fetch users filtered by user_type
  useEffect(() => {
    async function fetchUsers() {
      setLoadingUsers(true);
      try {
        const res = await axios.get(`${API_BASE}/api/users/users-users/?user_type_in=${USER_TYPES.join(",")}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setUsers(res.data);
      } catch (error) {
        console.error(error);
        Swal.fire("Error", "Failed to load users", "error");
      } finally {
        setLoadingUsers(false);
      }
    }
    fetchUsers();
  }, [token]);

  // Fetch all AdministrativeSalary records
  useEffect(() => {
    async function fetchRecords() {
      try {
        const res = await axios.get(`${API_BASE}/admin-salaries/`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setRecords(res.data);
      } catch (error) {
        console.error(error);
        Swal.fire("Error", "Failed to load records", "error");
      }
    }
    fetchRecords();
  }, [token]);

  // Handle form input change (Add form)
  function handleChange(e) {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  }

  // Handle Autocomplete user select (Add form)
  function handleUserChange(event, value) {
    setFormData(prev => ({ ...prev, user: value }));
  }

  // Submit form for create new record
  async function handleSubmit(e) {
    e.preventDefault();
    if (!formData.user) {
      Swal.fire("Validation error", "Please select a user", "warning");
      return;
    }
    setLoadingSubmit(true);

    const payload = {
      user_id: formData.user.id,
      qualification: formData.qualification,
      subjects: formData.subjects.split(",").map(s => s.trim()),
      grade_class: formData.grade_class.split(",").map(s => s.trim()),
      dependent_student: formData.dependent_student,
      contact: formData.contact,
      salary: formData.salary,
      date_add: formData.date_add,
    };

    try {
      const res = await axios.post(`${API_BASE}/admin-salaries/`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setRecords(prev => [res.data, ...prev]);
      Swal.fire("Success", "Record added successfully", "success");

      setTimeout(() => {
        setLoadingSubmit(false);
        setFormData({
          user: null,
          qualification: "",
          subjects: "",
          grade_class: "",
          dependent_student: "",
          contact: "",
          salary: "",
          date_add: "",
        });
      }, 3000);
    } catch (error) {
      setLoadingSubmit(false);
      console.error(error);
      Swal.fire("Error", "Failed to submit form", "error");
    }
  }

  // Open edit dialog with record data
  function handleEdit(record) {
    setEditingId(record.id);
    setEditFormData({
      user: users.find(u => u.id === record.user.id) || null,
      qualification: record.qualification,
      subjects: record.subjects.join(", "),
      grade_class: record.grade_class.join(", "),
      dependent_student: record.dependent_student,
      contact: record.contact,
      salary: record.salary,
      date_add: record.date_add,
    });
    setOpenDialog(true);
  }

  // Handle edit form input change
  function handleEditChange(e) {
    setEditFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  }

  // Handle edit Autocomplete user select
  function handleEditUserChange(event, value) {
    setEditFormData(prev => ({ ...prev, user: value }));
  }

  // Submit edit form
  async function handleEditSubmit(e) {
    e.preventDefault();
    if (!editFormData.user) {
      Swal.fire("Validation error", "Please select a user", "warning");
      return;
    }
    setLoadingEditSubmit(true);

    const payload = {
      user_id: editFormData.user.id,
      qualification: editFormData.qualification,
      subjects: editFormData.subjects.split(",").map(s => s.trim()),
      grade_class: editFormData.grade_class.split(",").map(s => s.trim()),
      dependent_student: editFormData.dependent_student,
      contact: editFormData.contact,
      salary: editFormData.salary,
      date_add: editFormData.date_add,
    };

    try {
      const res = await axios.put(`${API_BASE}/admin-salaries/${editingId}/`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setRecords(prev =>
        prev.map(rec => (rec.id === editingId ? res.data : rec))
      );
      Swal.fire("Success", "Record updated successfully", "success");

      setTimeout(() => {
        setLoadingEditSubmit(false);
        setOpenDialog(false);
        setEditingId(null);
      }, 3000);
    } catch (error) {
      setLoadingEditSubmit(false);
      console.error(error);
      Swal.fire("Error", "Failed to update record", "error");
    }
  }

  // Confirm and delete a record
  function handleDelete(id) {
    Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it!",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await axios.delete(`${API_BASE}/api/administrative-salaries/${id}/`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          setRecords(prev => prev.filter(r => r.id !== id));
          Swal.fire("Deleted!", "Record has been deleted.", "success");
        } catch (error) {
          console.error(error);
          Swal.fire("Error", "Failed to delete record", "error");
        }
      }
    });
  }

  return (
    <Box sx={{ maxWidth: 900, mx: "auto", p: 2 }}>
      <Typography variant="h5" mb={2}>
        Administrative Salary Form
      </Typography>

      {/* Add New Record Form */}
      <form onSubmit={handleSubmit}>
        <Autocomplete
          options={users}
          getOptionLabel={(option) => option.full_name || ""}
          value={formData.user}
          onChange={handleUserChange}
          loading={loadingUsers}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Select User"
              required
              variant="outlined"
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loadingUsers ? <CircularProgress color="inherit" size={20} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
          ListboxProps={{ style: { maxHeight: 200, overflow: 'auto' } }}
          sx={{ mb: 2 }}
        />

        <TextField
          label="Qualification"
          name="qualification"
          value={formData.qualification}
          onChange={handleChange}
          fullWidth
          required
          sx={{ mb: 2 }}
        />

        <TextField
          label="Subjects (comma separated)"
          name="subjects"
          value={formData.subjects}
          onChange={handleChange}
          fullWidth
          required
          sx={{ mb: 2 }}
        />

        <TextField
          label="Grade Class (comma separated)"
          name="grade_class"
          value={formData.grade_class}
          onChange={handleChange}
          fullWidth
          sx={{ mb: 2 }}
        />

        <TextField
          label="Dependent Student"
          name="dependent_student"
          value={formData.dependent_student}
          onChange={handleChange}
          fullWidth
          required
          sx={{ mb: 2 }}
        />

        <TextField
          label="Contact"
          name="contact"
          value={formData.contact}
          onChange={handleChange}
          fullWidth
          required
          sx={{ mb: 2 }}
        />

        <TextField
          label="Salary"
          name="salary"
          type="number"
          value={formData.salary}
          onChange={handleChange}
          fullWidth
          required
          sx={{ mb: 2 }}
          inputProps={{ step: "0.01" }}
        />

        <TextField
          label="Date Added"
          name="date_add"
          type="date"
          value={formData.date_add}
          onChange={handleChange}
          fullWidth
          required
          sx={{ mb: 2 }}
          InputLabelProps={{ shrink: true }}
        />

        <Button
          type="submit"
          variant="contained"
          color="primary"
          disabled={loadingSubmit}
          startIcon={loadingSubmit && <CircularProgress size={20} />}
        >
          {loadingSubmit ? "Submitting..." : "Submit"}
        </Button>
      </form>

      {/* Records Table */}
      <Box mt={4}>
        <Typography variant="h6" mb={1}>
          Administrative Salary Records
        </Typography>
        <TableContainer component={Paper}>
          <Table size="small" aria-label="salary records table">
            <TableHead>
              <TableRow>
                <TableCell>User</TableCell>
                <TableCell>Qualification</TableCell>
                <TableCell>Subjects</TableCell>
                <TableCell>Grade Class</TableCell>
                <TableCell>Dependent Student</TableCell>
                <TableCell>Contact</TableCell>
                <TableCell>Salary</TableCell>
                <TableCell>Date Added</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {records.map((rec) => (
                <TableRow key={rec.id}>
                  <TableCell>{rec.user?.full_name}</TableCell>
                  <TableCell>{rec.qualification}</TableCell>
                  <TableCell>{rec.subjects?.join(", ")}</TableCell>
                  <TableCell>{rec.grade_class?.join(", ")}</TableCell>
                  <TableCell>{rec.dependent_student}</TableCell>
                  <TableCell>{rec.contact}</TableCell>
                  <TableCell>{rec.salary}</TableCell>
                  <TableCell>{rec.date_add}</TableCell>
                  <TableCell align="center">
                    <IconButton
                      color="primary"
                      onClick={() => handleEdit(rec)}
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      color="error"
                      onClick={() => handleDelete(rec.id)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
              {records.length === 0 && (
                <TableRow>
                  <TableCell colSpan={9} align="center">
                    No records found.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      {/* Edit Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Administrative Salary</DialogTitle>
        <DialogContent>
          <Box
            component="form"
            onSubmit={handleEditSubmit}
            noValidate
            sx={{ mt: 1 }}
          >
            <Autocomplete
              options={users}
              getOptionLabel={(option) => option.full_name || ""}
              value={editFormData.user}
              onChange={handleEditUserChange}
              loading={loadingUsers}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select User"
                  required
                  variant="outlined"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loadingUsers ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                  sx={{ mb: 2 }}
                />
              )}
              ListboxProps={{ style: { maxHeight: 200, overflow: 'auto' } }}
            />

            <TextField
              label="Qualification"
              name="qualification"
              value={editFormData.qualification}
              onChange={handleEditChange}
              fullWidth
              required
              sx={{ mb: 2 }}
            />

            <TextField
              label="Subjects (comma separated)"
              name="subjects"
              value={editFormData.subjects}
              onChange={handleEditChange}
              fullWidth
              required
              sx={{ mb: 2 }}
            />

            <TextField
              label="Grade Class (comma separated)"
              name="grade_class"
              value={editFormData.grade_class}
              onChange={handleEditChange}
              fullWidth
              sx={{ mb: 2 }}
            />

            <TextField
              label="Dependent Student"
              name="dependent_student"
              value={editFormData.dependent_student}
              onChange={handleEditChange}
              fullWidth
              required
              sx={{ mb: 2 }}
            />

            <TextField
              label="Contact"
              name="contact"
              value={editFormData.contact}
              onChange={handleEditChange}
              fullWidth
              required
              sx={{ mb: 2 }}
            />

            <TextField
              label="Salary"
              name="salary"
              type="number"
              value={editFormData.salary}
              onChange={handleEditChange}
              fullWidth
              required
              sx={{ mb: 2 }}
              inputProps={{ step: "0.01" }}
            />

            <TextField
              label="Date Added"
              name="date_add"
              type="date"
              value={editFormData.date_add}
              onChange={handleEditChange}
              fullWidth
              required
              sx={{ mb: 2 }}
              InputLabelProps={{ shrink: true }}
            />

            <DialogActions>
              <Button onClick={() => setOpenDialog(false)} disabled={loadingEditSubmit}>
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={loadingEditSubmit}
                startIcon={loadingEditSubmit && <CircularProgress size={20} />}
              >
                {loadingEditSubmit ? "Updating..." : "Update"}
              </Button>
            </DialogActions>
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
}
